// Description: Log of every change to inventory quantities

import React, { useContext, useEffect, useState } from 'react'
import { Container, Table, Row, Col, Button, Form } from 'react-bootstrap'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Loader from '../components/Loader'
import Message from '../components/Message'
import ReportPager from '../components/ReportPager'
import { getStockMovements } from '../actions/reportActions'
import { ThemeContext } from '../components/ThemeContext.js'
import {
  EMPLOYEE_PERMISSIONS,
  hasEmployeePermission
} from '../constants/employeePermissions.js'

const TYPE_LABELS = {
  initial: 'Item added',
  manual: 'Manual adjustment',
  sale: 'Sale',
  return: 'Return',
  reset: 'Reset to 10',
  delete: 'Item deleted'
}

const StockMovements = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme } = useContext(ThemeContext)

  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [type, setType] = useState('')
  const [search, setSearch] = useState('')
  const [keyword, setKeyword] = useState('')
  const [pageNumber, setPageNumber] = useState(1)

  const { loading, error, data } = useSelector((state) => state.stockMovements)
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  const canViewReports = hasEmployeePermission(
    userInfo,
    ownerInfo,
    EMPLOYEE_PERMISSIONS.REPORTS_VIEW
  )

  useEffect(() => {
    if (!userInfo && !ownerInfo) {
      navigate('/login')
    } else if (!canViewReports) {
      navigate('/pos')
    } else {
      dispatch(getStockMovements({ from, to, type, keyword, pageNumber }))
    }
  }, [
    dispatch,
    navigate,
    userInfo,
    ownerInfo,
    canViewReports,
    from,
    to,
    type,
    keyword,
    pageNumber
  ])

  const filterChange = (setter) => (e) => {
    setter(e.target.value)
    setPageNumber(1)
  }

  const submitSearch = (e) => {
    e.preventDefault()
    setKeyword(search.trim())
    setPageNumber(1)
  }

  const movements = data?.movements || []

  return (
    <Container className='my-3 report-page' style={{ color: theme.text }}>
      <Row className='align-items-center report-page-heading'>
        <Col xs={12} md>
          <h1>Inventory adjustments</h1>
          <p>Track stock changes and the reason for each adjustment.</p>
        </Col>
        <Col xs={12} md='auto' className='text-md-end report-page-actions'>
          <Button
            size='sm'
            className='my-3'
            variant='outline-primary'
            onClick={() => navigate('/reports')}
          >
            Go Back
          </Button>
        </Col>
      </Row>

      <Form
        className='report-filter-panel d-flex flex-wrap align-items-end'
        onSubmit={submitSearch}
      >
        <Form.Group>
          <Form.Label className='mb-1'>From</Form.Label>
          <Form.Control
            type='date'
            value={from}
            onChange={filterChange(setFrom)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className='mb-1'>To</Form.Label>
          <Form.Control type='date' value={to} onChange={filterChange(setTo)} />
        </Form.Group>
        <Form.Group>
          <Form.Label className='mb-1'>Type</Form.Label>
          <Form.Control
            as='select'
            value={type}
            onChange={filterChange(setType)}
          >
            <option value=''>All types</option>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
        <Form.Group className='report-filter-search'>
          <Form.Label className='mb-1'>Item</Form.Label>
          <Form.Control
            type='text'
            placeholder='Item, maker, UPC'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Form.Group>
        <Button type='submit' variant='primary'>
          Search
        </Button>
        <small className='report-filter-hint'>
          Leave dates empty for the last 30 days.
        </small>
      </Form>

      {loading && <Loader />}
      {error && <Message variant='danger'>{error}</Message>}

      {data && (
        <>
          <Table
            striped
            bordered
            hover
            responsive
            size='sm'
            className='my-3 report-table'
          >
            <thead>
              <tr>
                <th>Date</th>
                <th>Item</th>
                <th>Type</th>
                <th>Change</th>
                <th>Qty after</th>
                <th>By</th>
                <th>Reference / note</th>
              </tr>
            </thead>
            <tbody>
              {movements.length === 0 && (
                <tr>
                  <td colSpan={7}>No stock changes found.</td>
                </tr>
              )}
              {movements.map((m) => (
                <tr key={m._id}>
                  <td>{new Date(m.createdAt).toLocaleString()}</td>
                  <td>
                    {m.manufacturer} {m.subname} - {m.item}
                  </td>
                  <td>{TYPE_LABELS[m.type] || m.type}</td>
                  <td>{m.change > 0 ? `+${m.change}` : m.change}</td>
                  <td>{m.qtyAfter}</td>
                  <td>{m.by?.name}</td>
                  <td>
                    {[m.reference && `Receipt ${m.reference}`, m.note]
                      .filter(Boolean)
                      .join(' - ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <small>{data.count} changes.</small>
          <ReportPager
            page={data.page}
            pages={data.pages}
            onChange={setPageNumber}
          />
        </>
      )}
    </Container>
  )
}

export default StockMovements
